import { resolveLandingMedia } from "./landingMedia";

function upsertMeta(selector, attributes, content) {
  let element = document.head.querySelector(selector);
  const normalized = String(content || "").trim();

  if (!normalized) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.setAttribute("content", normalized);
}

function upsertLink(selector, rel, href, extra = {}) {
  let element = document.head.querySelector(selector);
  const normalized = String(href || "").trim();

  if (!normalized) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    Object.entries(extra).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.href = normalized;
}

function syncDynamicFonts(theme = {}) {
  const googleFontsUrl = String(theme?.googleFontsUrl || "").trim();
  let link = document.querySelector('link[data-storefront-fonts="google"]');
  if (googleFontsUrl) {
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.dataset.storefrontFonts = "google";
      document.head.appendChild(link);
    }
    link.href = googleFontsUrl;
  } else if (link) {
    link.remove();
  }

  const fonts = Array.isArray(theme?.fonts) ? theme.fonts : [];
  let style = document.querySelector('style[data-storefront-fonts="custom"]');
  if (!fonts.length) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement("style");
    style.dataset.storefrontFonts = "custom";
    document.head.appendChild(style);
  }
  style.textContent = fonts
    .map((font) => {
      const family = String(font.family || "").replace(/[{};]/g, "");
      const url = resolveLandingMedia(font.url);
      const weight = String(font.weight || "400").replace(/[^0-9 ]/g, "") || "400";
      const fontStyle = ["normal", "italic", "oblique"].includes(font.style)
        ? font.style
        : "normal";
      const format = String(font.format || "woff2").replace(/[^a-z0-9-]/gi, "");
      return `@font-face{font-family:"${family}";src:url("${url}") format("${format}");font-weight:${weight};font-style:${fontStyle};font-display:swap;}`;
    })
    .join("\n");
}

function manifestDataUrl({ name, icon, themeColor, backgroundColor }) {
  const manifest = {
    short_name: String(name || "Sitio").slice(0, 24),
    name: String(name || "Sitio"),
    start_url: ".",
    display: "standalone",
    theme_color: themeColor || "#111111",
    background_color: backgroundColor || "#ffffff",
    icons: icon
      ? [
          {
            src: icon,
            sizes: "any",
            purpose: "any maskable",
          },
        ]
      : [],
  };

  return `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
}

export function syncDocumentBranding(landing = {}) {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  const business = landing.business || {};
  const theme = landing.theme || {};
  const seo = landing.seo || {};
  const title = String(
    seo.title || business.name || landing?.project?.name || "WebProfit"
  ).trim();
  const description = String(
    seo.description || landing.hero?.subtitle || landing.hero?.description || ""
  ).trim();
  const canonicalUrl = `${window.location.origin}${window.location.pathname || "/"}`;
  const seoImage = resolveLandingMedia(seo.imageUrl || landing.hero?.imageUrl);
  const serverSocialImage = String(
    document.head.querySelector('meta[property="og:image"]')?.getAttribute("content") || ""
  ).trim();
  const socialImage = serverSocialImage.startsWith(`${window.location.origin}/social-preview.jpg`)
    ? serverSocialImage
    : seoImage;
  const logo = resolveLandingMedia(theme.logoUrl);
  const compactLogo = resolveLandingMedia(theme.compactLogoUrl || theme.logoUrl);
  const favicon = resolveLandingMedia(theme.faviconUrl || compactLogo || logo);
  const themeColor = String(theme.primaryColor || "#111111");
  const backgroundColor = String(theme.backgroundColor || "#ffffff");

  document.title = title;
  document.documentElement.lang = "es";

  upsertMeta('meta[name="description"]', { name: "description" }, description);
  upsertMeta('meta[name="author"]', { name: "author" }, business.name);
  upsertMeta('meta[name="theme-color"]', { name: "theme-color" }, themeColor);
  upsertMeta('meta[property="og:title"]', { property: "og:title" }, title);
  upsertMeta('meta[property="og:description"]', { property: "og:description" }, description);
  upsertMeta('meta[property="og:image"]', { property: "og:image" }, socialImage);
  upsertMeta('meta[property="og:image:url"]', { property: "og:image:url" }, socialImage);
  upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url" }, socialImage);
  upsertMeta('meta[property="og:image:type"]', { property: "og:image:type" }, socialImage ? "image/jpeg" : "");
  upsertMeta('meta[property="og:image:width"]', { property: "og:image:width" }, socialImage ? "1200" : "");
  upsertMeta('meta[property="og:image:height"]', { property: "og:image:height" }, socialImage ? "630" : "");
  upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt" }, socialImage ? title : "");
  upsertMeta('meta[property="og:type"]', { property: "og:type" }, "website");
  upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, business.name);
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, socialImage ? "summary_large_image" : "summary");
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, socialImage);
  upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt" }, socialImage ? title : "");

  upsertLink('link[rel="canonical"]', "canonical", canonicalUrl);
  upsertLink('link[rel="icon"]', "icon", favicon);
  upsertLink('link[rel="shortcut icon"]', "shortcut icon", favicon);
  upsertLink('link[rel="apple-touch-icon"]', "apple-touch-icon", compactLogo || favicon);
  upsertLink(
    'link[rel="manifest"]',
    "manifest",
    manifestDataUrl({
      name: business.name || title,
      icon: compactLogo || favicon || logo,
      themeColor,
      backgroundColor,
    })
  );

  syncDynamicFonts(theme);
}
